import { TrabajadorEntity } from '../entity/trabajador.entity';
import { Injectable } from '@angular/core';
import { environment } from "../../../environments/environment";
import { CommonResponse } from "../response/common.response";
import { ApiTrabajadorRepository } from "../../data/datasource/api/trabajador/api.trabajador.repository";
import { UtilityService } from "../services/utility.service";
import { LocalUsuarioRepository } from "../../data/datasource/local/usuario/local.usuario.repository";
import {from, Observable} from "rxjs";
import {map} from "rxjs/operators";

@Injectable({
    providedIn: 'root'
})
export class Trabajador extends TrabajadorEntity {
    PARAM = environment.PARAM;

    constructor(
        public apiTrabajadorRepository?: ApiTrabajadorRepository,
        public localTrabajadorRepository?: LocalUsuarioRepository
    ) {
        super();
    }

    /**
     * Armar el objeto en base a sus atributos: valor
     * Valido si el service 'TrabajadorService' esta inyectado y es un objeto,
     * para pasarlo como parametro en el constructor
     */
    public static build(data: {}, apiTrabajadorRepository?: ApiTrabajadorRepository) {
        const trabajador = new Trabajador();
        trabajador.apiTrabajadorRepository = apiTrabajadorRepository;
        // tslint:disable-next-line:forin
        for (const propiedad in data) {
            trabajador[propiedad] = data[propiedad];
        }
        return trabajador;
    }

    public async login(credenciales: any): Promise<CommonResponse> {
        const response = await this.apiTrabajadorRepository.login(credenciales);
        if (response.data) {
            if (Array.isArray(response.data)) {
                response.data = response.data
                    // @ts-ignore
                    .map(Trabajador.build)
                    .filter(Boolean);
            } else {
                response.data = Trabajador.build(response.data);
            }
        }
        return response;
    }

    public guardarSesion(sesionData) {
        if (sesionData.token) {
            this.localTrabajadorRepository.guardarUsuarioSesion(sesionData);
            this.localTrabajadorRepository.guardarToken(sesionData.token);
        }
    }

    public async deleteTokenByToken(): Promise<CommonResponse> {
        const response = await this.apiTrabajadorRepository.deleteTokenByToken(this.localTrabajadorRepository.obtenerToken());
        if (response.data) {
            if (Array.isArray(response.data)) {
                response.data = response.data
                    // @ts-ignore
                    .map(Trabajador.build)
                    .filter(Boolean);
            } else {
                response.data = Trabajador.build(response.data);
            }
        }
        return response;
    }

    public async obtenerListaPorPaginacion(obj: any): Promise<any> {
        const response = await this.apiTrabajadorRepository.obtenerListaPorPaginacion(obj);

        if (response.data) {
            // Check if response.data has a nested data property (pagination structure)
            if (response.data.data && Array.isArray(response.data.data)) {
                response.data.data = response.data.data
                    .map(Trabajador.build)
                    .filter(Boolean);
            } else if (Array.isArray(response.data)) {
                response.data = response.data
                    .map(Trabajador.build)
                    .filter(Boolean);
            } else {
                response.data = Trabajador.build(response.data);
            }
        }

        return response;
    }

    public busquedaTrabajadores(obj: any): Observable<any> {
        return from(
            this.apiTrabajadorRepository.obtenerListaPorPaginacion(obj)
        ).pipe(
            map((response: any) => {
                const data = response?.data;

                let trabajadores: any[] = [];

                if (Array.isArray(data?.data)) {
                    // Caso paginado con data.data
                    trabajadores = data.data;
                } else if (Array.isArray(data)) {
                    // Caso array directo
                    trabajadores = data;
                } else if (data) {
                    // Caso objeto único
                    trabajadores = [data];
                }

                // @ts-ignore
                const trabajadoresTransformados = trabajadores
                    .map(t => Trabajador.build(t))
                    .filter(Boolean);


                return {
                    ...response,
                    data: trabajadoresTransformados
                };
            })
        );
    }

    public buscarPorUbigeo(obj: any): Observable<any> {
        return from(
            this.apiTrabajadorRepository.buscarPorUbigeo(obj)
        ).pipe(
            map((response: any) => {
                const ubigeos: any[] = this.castearDatArray(response?.data);

                ubigeos.forEach((ubigeo: any) => {
                    ubigeo = this.castearUbigeo(ubigeo);
                });

                return {
                    ...response,
                    data: ubigeos
                };
            })
        );
    }

    public buscarPorProfesion(obj: any): Observable<any> {
        return from(
            this.apiTrabajadorRepository.buscarPorProfesion(obj)
        ).pipe(
            map((response: any) => {
                const profesiones: any[] = this.castearDatArray(response?.data);
                return {
                    ...response,
                    data: profesiones
                };
            })
        );
    }

    castearDatArray(data: any): any {
        let dataList: any[] = [];
        if (Array.isArray(data?.data)) {
            // Caso paginado con data.data
            dataList = data.data;
        } else if (Array.isArray(data)) {
            // Caso array directo
            dataList = data;
        } else if (data) {
            // Caso objeto único
            dataList = [data];
        }
        return dataList;
    }

    castearUbigeo(item: any): any {
        const partes = item.nombre.split(' - ');
        item.departamento = partes[0];
        item.provincia = partes[1];
        item.distrito = partes[2];
        item.displayText = item.departamento + " - " + item.provincia + " - " + item.distrito;
        return item;
    }

    public async registrar(objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.registrar(objeto);
    }

    public async editar(objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.editar(objeto);
    }

    public async inactivar(id: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.inactivar(id);
    }

    public async activar(id: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.activar(id);
    }

    public async habilitar(id: any, empresaId?: number): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.habilitar(id, empresaId);
    }

    public async getPuestos(): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getPuestos();
    }

    public async getSedes(): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getSedes();
    }
    public async getTipificaciones(): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getTipificaciones();
    }
    public async getEstadoCivilList(): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getEstadoCivilList();
    }

    /** Catalogo de SUNAT para el documento y la nacionalidad: se pide una vez por sesion. */
    private static catalogosIdentidad: Promise<CommonResponse> = null;
    public getCatalogosIdentidad(): Promise<CommonResponse> {
        if (!Trabajador.catalogosIdentidad) {
            Trabajador.catalogosIdentidad = this.apiTrabajadorRepository.getCatalogosIdentidad().then((response: any) => {
                if (response.tipo != environment.HTTP_RESPONSE.SUCCESS) {
                    Trabajador.catalogosIdentidad = null;   // reintenta la proxima vez
                }
                return response;
            });
        }
        return Trabajador.catalogosIdentidad;
    }
    public async getEmpresasList(): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getEmpresasList();
    }
    public async getGradoInstruccionList(): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getGradoInstruccionList();
    }
    public async getDocumentos(): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getDocumentos();
    }
    public async getTipificacionesAsistencia(): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getTipificacionesAsistencia();
    }
    public async getHistorialVacaciones(trabajadorID, pagina, registros, empresaId: any = this.PARAM.TODOS  ): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getHistorialVacaciones(trabajadorID, pagina, registros, empresaId);
    }

    public async getHistorialDocumentos(trabajadorID, tipoDoc, pagina, registros, empresaId): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getHistorialDocumentos(trabajadorID, tipoDoc, pagina, registros, empresaId);
    }

    // Multas
    public async getHistorialMultas(trabajadorID, pagina, registros, empresaId): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getHistorialMultas(trabajadorID, pagina, registros, empresaId);
    }

    public async getDataMulta(multaid): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getDataMulta(multaid);
    }

    public async registrarMulta(objeto): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.registrarMulta(objeto);
    }

    public async eliminarMulta(id): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.eliminarMulta(id);
    }

    public async generarConvenioPrestamo(multaid): Promise<Blob> {
        return await this.apiTrabajadorRepository.generarConvenioPrestamo(multaid);
    }

    public async generarConvenioResponsabilidad(multaid): Promise<Blob> {
        return await this.apiTrabajadorRepository.generarConvenioResponsabilidad(multaid);
    }

    // Cupones
    public async getHistorialCupones(trabajadorID, pagina, registros, empresaId: any = this.PARAM.TODOS): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getHistorialCupones(trabajadorID, pagina, registros, empresaId);
    }

    // Descanso Medico
    public async getSolicitudes_DescandoMedico(trabajadorID, pagina, registros, empresaId: any = this.PARAM.TODOS )
        : Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getSolicitudes_DescandoMedico(trabajadorID, pagina, registros, empresaId);
    }

    // Permiso
    public async getSolicitudes_Permiso(trabajadorID, pagina, registros, empresaId: any = this.PARAM.TODOS )
        : Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getSolicitudes_Permiso(trabajadorID, pagina, registros, empresaId);
    }
    public async getTipoPermisoByGrupo(solicitudGrupo)
        : Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getTipoPermisoByGrupo(solicitudGrupo);
    }

    public async registrarDocumento(objeto): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.registrarDocumento(objeto);
    }

    public async getAreas(): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getAreas();
    }

    public async getDataDashboard(objEnvio): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getDataDashboard(objEnvio);
    }

    public async getDataCumpleanios(objEnvio): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getDataCumpleanios(objEnvio);
    }

    public async getPermisos(): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getPermisos();
    }

    public async getImagenSaludoCumpleanios(trabajadorId: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getImagenSaludoCumpleanios(trabajadorId);
    }

    public async getSintomasControlDiario(trabajadorid): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getSintomasControlDiario(trabajadorid);
    }
    public async registrarSeguimiento(trabajadorid, objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.registrarSeguimiento(trabajadorid, objeto);
    }
    public async editarSeguimiento(seguimientoid, objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.editarSeguimiento(seguimientoid, objeto);
    }
    public async obtenerListaPorPaginacionSeguimiento(busqueda: any, pagina: any, registros: any, objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.obtenerListaPorPaginacionSeguimiento(busqueda, pagina, registros, objeto);
    }
    public async getbyID(trabajadorid): Promise<CommonResponse> {
        const response = await this.apiTrabajadorRepository.getbyID(trabajadorid);
        if (response.data) {
            if (Array.isArray(response.data)) {
                response.data = response.data
                    // @ts-ignore
                    .map(Trabajador.build)
                    .filter(Boolean);
            } else {
                response.data = Trabajador.build(response.data);
            }
        }
        return response;
    }
    public async getByIdAndEmpresa(trabajadorid, empresaId): Promise<CommonResponse> {
        const response = await this.apiTrabajadorRepository.getByIdAndEmpresa(trabajadorid, empresaId);
        if (response.data) {
            if (Array.isArray(response.data)) {
                response.data = response.data
                    // @ts-ignore
                    .map(Trabajador.build)
                    .filter(Boolean);
            } else {
                response.data = Trabajador.build(response.data);
            }
        }
        return response;
    }

    public async getHistorialSeguimiento(trabajadorid: any, pagina: any, registros: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getHistorialSeguimiento(trabajadorid, pagina, registros);
    }
    public async getSeguimiento(seguimientoid: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getSeguimiento(seguimientoid);
    }

    public async darAlta(seguimientoid: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.darAlta(seguimientoid);
    }

    public async adjuntarArchivo(file: FormData): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.adjuntarArchivo(file);
    }

    public async reporteCesePaginado(objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.reporteCesePaginado(objeto);
    }

    public async procesarInformacionExcel(objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.procesarInformacionExcel(objeto);
    }

    public async procesarDataTrabajadorExcel(objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.procesarDataTrabajadorExcel(objeto);
    }

    public async comorbilidadList(trabajadorid): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.comorbilidadList(trabajadorid);
    }

    public async addComorbilidad(objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.addComorbilidad(objeto);
    }

    public async deleteComorbilidad(id: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.deleteComorbilidad(id);
    }

    public async agregarDias(trajadorid, objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.agregarDias(trajadorid, objeto);
    }
    public async agregarConfiguracionAsistencia(trajadorid, objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.agregarConfiguracionAsistencia(trajadorid, objeto);
    }

    public async getDias(trajadorid): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getDias(trajadorid);
    }
    public async getConfiguracionAsistencia(trajadorid): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getConfiguracionAsistencia(trajadorid);
    }

    public async getTiposPermisoTrabajador(): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getTiposPermisoTrabajador();
    }

    public async actualizarFechaMarcacion(objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.actualizarFechaMarcacion(objeto);
    }

    public async enviarCorreoAltaPersonal(): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.enviarCorreoAltaPersonal();
    }

    public async enviarCorreoCesePersonal(trabajadorid: any, empresaId: any = null): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.enviarCorreoCesePersonal(trabajadorid, empresaId);
    }

    public async buscarEnGrupo(numero: string, empresaId: any = null): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.buscarEnGrupo(numero, empresaId);
    }

    public async generarFotocheck(trabajadorid: any, empresaId: any = null): Promise<Blob> {
        return await this.apiTrabajadorRepository.generarFotocheck(trabajadorid, empresaId);
    }

    public async validarFotocheck(dni: string): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.validarFotocheck(dni);
    }

    public async reenviarCorreoCesePersonal(trabajadorid: any, empresaId: any = null): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.reenviarCorreoCesePersonal(trabajadorid, empresaId);
    }

    public async getDocumentosByTrabajador(trabajadorid: any, empresaId: any = environment.PARAM.TODOS): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getDocumentosByTrabajador(trabajadorid, empresaId);
    }

    public async deleteArchivo(documentoId: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.deleteArchivo(documentoId);
    }
    public async getChatList(objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getChatList(objeto);
    }
    public async getPictureContact(numero: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.getPictureContact(numero);
    }

    public async agregarDocumento(objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.agregarDocumento(objeto);
    }

    public async eliminarDocumento(documentoid: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.eliminarDocumento(documentoid);
    }

    public async buscarPorDni(dni: string): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.buscarPorDni(dni);
    }

    public async asignarEmpresa(objeto: any): Promise<CommonResponse> {
        return await this.apiTrabajadorRepository.asignarEmpresa(objeto);
    }


    public cerrarSesion() {
        this.localTrabajadorRepository.eliminarUsuarioSesion();
        this.localTrabajadorRepository.eliminarToken();
    }

    /**
     * Valida si el producto tiene un id valido
     */
    public tieneIdValido() {
        return UtilityService.esUnIDValido(this.trabajador_id);
    }

    /**
     * Valida si el objeto esta activo
     */
    public estaActivo() {
        // tslint:disable-next-line:triple-equals
        return this.trabajador_estado == this.PARAM.ACTIVO;
    }

    /**
     * Valida si el objeto esta activo
     */
    public estadoDescripcion() {
        // tslint:disable-next-line:triple-equals
        return this.estaActivo() ? 'Activo' : 'Inactivo';
    }

    /**
     * Resetar el objeto con valores por defecto
     */
    public reset() {
        this.setTrabajador_estado(this.PARAM.ACTIVO);
    }

    public searchTrabajadorListByJefe(obj: any): Observable<any> {
        return from(
            this.apiTrabajadorRepository.searchTrabajadorListByJefe(obj)
        ).pipe(
            map((response: any) => {
                const data = response?.data;

                let trabajadores: any[] = [];

                if (Array.isArray(data?.data)) {
                    // Caso paginado con data.data
                    trabajadores = data.data;
                } else if (Array.isArray(data)) {
                    // Caso array directo
                    trabajadores = data;
                } else if (data) {
                    // Caso objeto único
                    trabajadores = [data];
                }

                // @ts-ignore
                const trabajadoresTransformados = trabajadores
                    .map(t => Trabajador.build(t))
                    .filter(Boolean);


                return {
                    ...response,
                    data: trabajadoresTransformados
                };
            })
        );
    }

}
